/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name itercugmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cugmean}
*/
import itercugmean from './../../iter/cugmean';
setReadOnly( ns, 'itercugmean', itercugmean );

/**
* @name itercuhmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cuhmean}
*/
import itercuhmean from './../../iter/cuhmean';
setReadOnly( ns, 'itercuhmean', itercuhmean );

/**
* @name itercumax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cumax}
*/
import itercumax from './../../iter/cumax';
setReadOnly( ns, 'itercumax', itercumax );

/**
* @name itercumaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cumaxabs}
*/
import itercumaxabs from './../../iter/cumaxabs';
setReadOnly( ns, 'itercumaxabs', itercumaxabs );

/**
* @name itercumean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cumean}
*/
import itercumean from './../../iter/cumean';
setReadOnly( ns, 'itercumean', itercumean );

/**
* @name itercumeanabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cumeanabs}
*/
import itercumeanabs from './../../iter/cumeanabs';
setReadOnly( ns, 'itercumeanabs', itercumeanabs );

/**
* @name itercumeanabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cumeanabs2}
*/
import itercumeanabs2 from './../../iter/cumeanabs2';
setReadOnly( ns, 'itercumeanabs2', itercumeanabs2 );

/**
* @name itercumidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cumidrange}
*/
import itercumidrange from './../../iter/cumidrange';
setReadOnly( ns, 'itercumidrange', itercumidrange );

/**
* @name itercumin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cumin}
*/
import itercumin from './../../iter/cumin';
setReadOnly( ns, 'itercumin', itercumin );

/**
* @name itercuminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cuminabs}
*/
import itercuminabs from './../../iter/cuminabs';
setReadOnly( ns, 'itercuminabs', itercuminabs );

/**
* @name itercuprod
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cuprod}
*/
import itercuprod from './../../iter/cuprod';
setReadOnly( ns, 'itercuprod', itercuprod );

/**
* @name itercurange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/curange}
*/
import itercurange from './../../iter/curange';
setReadOnly( ns, 'itercurange', itercurange );

/**
* @name itercusum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cusum}
*/
import itercusum from './../../iter/cusum';
setReadOnly( ns, 'itercusum', itercusum );

/**
* @name itercusumabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cusumabs}
*/
import itercusumabs from './../../iter/cusumabs';
setReadOnly( ns, 'itercusumabs', itercusumabs );

/**
* @name itercusumabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/cusumabs2}
*/
import itercusumabs2 from './../../iter/cusumabs2';
setReadOnly( ns, 'itercusumabs2', itercusumabs2 );

/**
* @name itermax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/max}
*/
import itermax from './../../iter/max';
setReadOnly( ns, 'itermax', itermax );

/**
* @name itermaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/maxabs}
*/
import itermaxabs from './../../iter/maxabs';
setReadOnly( ns, 'itermaxabs', itermaxabs );

/**
* @name itermean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mean}
*/
import itermean from './../../iter/mean';
setReadOnly( ns, 'itermean', itermean );

/**
* @name itermeanabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/meanabs}
*/
import itermeanabs from './../../iter/meanabs';
setReadOnly( ns, 'itermeanabs', itermeanabs );

/**
* @name itermeanabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/meanabs2}
*/
import itermeanabs2 from './../../iter/meanabs2';
setReadOnly( ns, 'itermeanabs2', itermeanabs2 );

/**
* @name itermidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/midrange}
*/
import itermidrange from './../../iter/midrange';
setReadOnly( ns, 'itermidrange', itermidrange );

/**
* @name itermin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/min}
*/
import itermin from './../../iter/min';
setReadOnly( ns, 'itermin', itermin );

/**
* @name iterminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/minabs}
*/
import iterminabs from './../../iter/minabs';
setReadOnly( ns, 'iterminabs', iterminabs );

/**
* @name itermmax
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mmax}
*/
import itermmax from './../../iter/mmax';
setReadOnly( ns, 'itermmax', itermmax );

/**
* @name itermmaxabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mmaxabs}
*/
import itermmaxabs from './../../iter/mmaxabs';
setReadOnly( ns, 'itermmaxabs', itermmaxabs );

/**
* @name itermmean
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mmean}
*/
import itermmean from './../../iter/mmean';
setReadOnly( ns, 'itermmean', itermmean );

/**
* @name itermmeanabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mmeanabs}
*/
import itermmeanabs from './../../iter/mmeanabs';
setReadOnly( ns, 'itermmeanabs', itermmeanabs );

/**
* @name itermmeanabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mmeanabs2}
*/
import itermmeanabs2 from './../../iter/mmeanabs2';
setReadOnly( ns, 'itermmeanabs2', itermmeanabs2 );

/**
* @name itermmidrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mmidrange}
*/
import itermmidrange from './../../iter/mmidrange';
setReadOnly( ns, 'itermmidrange', itermmidrange );

/**
* @name itermmin
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mmin}
*/
import itermmin from './../../iter/mmin';
setReadOnly( ns, 'itermmin', itermmin );

/**
* @name itermminabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mminabs}
*/
import itermminabs from './../../iter/mminabs';
setReadOnly( ns, 'itermminabs', itermminabs );

/**
* @name itermprod
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mprod}
*/
import itermprod from './../../iter/mprod';
setReadOnly( ns, 'itermprod', itermprod );

/**
* @name itermrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/mrange}
*/
import itermrange from './../../iter/mrange';
setReadOnly( ns, 'itermrange', itermrange );

/**
* @name itermsum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/msum}
*/
import itermsum from './../../iter/msum';
setReadOnly( ns, 'itermsum', itermsum );

/**
* @name itermsumabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/msumabs}
*/
import itermsumabs from './../../iter/msumabs';
setReadOnly( ns, 'itermsumabs', itermsumabs );

/**
* @name itermsumabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/msumabs2}
*/
import itermsumabs2 from './../../iter/msumabs2';
setReadOnly( ns, 'itermsumabs2', itermsumabs2 );

/**
* @name iterprod
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/prod}
*/
import iterprod from './../../iter/prod';
setReadOnly( ns, 'iterprod', iterprod );

/**
* @name iterrange
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/range}
*/
import iterrange from './../../iter/range';
setReadOnly( ns, 'iterrange', iterrange );

/**
* @name iterstdev
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/stdev}
*/
import iterstdev from './../../iter/stdev';
setReadOnly( ns, 'iterstdev', iterstdev );

/**
* @name itersum
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/sum}
*/
import itersum from './../../iter/sum';
setReadOnly( ns, 'itersum', itersum );

/**
* @name itersumabs
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/sumabs}
*/
import itersumabs from './../../iter/sumabs';
setReadOnly( ns, 'itersumabs', itersumabs );

/**
* @name itersumabs2
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/sumabs2}
*/
import itersumabs2 from './../../iter/sumabs2';
setReadOnly( ns, 'itersumabs2', itersumabs2 );

/**
* @name itervariance
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/iter/variance}
*/
import itervariance from './../../iter/variance';
setReadOnly( ns, 'itervariance', itervariance );


// EXPORTS //

export default ns;
